import React, { PropsWithChildren, FC, useState, useEffect, useCallback } from "react";

import {
    Button,
    Input,
    LoadingSpinner,
    PageAlert,
    SmartDropdown,
} from "@xapp/components";

import { TEMPLATES } from "constants/templates";

import { OnboardingProps } from "../models";

export interface SimpleConfigureAndCreateStepProps {
    /**
     * Site data used to prepopulate fields
     */
    initialProps?: OnboardingProps;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional welcome message to be displayed to the user
     */
    welcomeMessage?: string | JSX.Element;
    /**
     * Is the app being created
     */
    creating?: boolean;
    /**
     * Status message displayed to the user to keep them updated on the creation process
     */
    creatingStatusMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string, message: string };
    /**
     * Show beta features
     */
    beta?: boolean;
    /**
     * The user has requested to create an app based on the provided properties.
     */
    onCreate(props: OnboardingProps): void;
    /**
     * The user wants to go back
     */
    onBack?(): void;
}

export const SimpleConfigureAndCreateStep: FC<PropsWithChildren<SimpleConfigureAndCreateStepProps>> = props => {

    const {
        className,
        creating,
        creatingStatusMessage,
        error,
        initialProps,
        onBack,
        onCreate,
        style,
        welcomeMessage
    } = props;

    // We get initial props from website loads or
    const [createProps, setCreateProps] = useState<OnboardingProps>(initialProps);

    // const [activeAccordian, setActiveAccordian] = useState<string>("");
    // const [surveyWebsite, setSurveyWebsite] = useState<boolean>(false);
    // const [surveyGBM, setSurveyGBM] = useState<boolean>(false);
    // const [surveyOther, setSurveyOther] = useState<boolean>(false);

    useEffect(() => {
        if (initialProps) {
            setCreateProps({
                ...initialProps
            });
        }
    }, [initialProps, setCreateProps])

    const createSetter = useCallback((key: keyof Omit<OnboardingProps, "template">) => {
        return (value: string) => {
            setCreateProps({
                ...createProps,
                [key]: value
            })
        }
    }, [setCreateProps, createProps]);

    const handleBusinessNameChange = createSetter("name");

    const handleCreateClick = useCallback(() => {
        onCreate(createProps as OnboardingProps);
    }, [createProps, onCreate]);

    const handleTemplateChange = useCallback((template) => {
        setCreateProps({
            ...createProps,
            template,
            beta: {
                ...createProps.beta,
                onboarding: {
                    template
                }
            }
        });
    }, [setCreateProps, createProps]);

    const handleOtherIndustryChange = useCallback((industry: string) => {
        setCreateProps({
            ...createProps,
            beta: {
                ...createProps.beta,
                onboarding: {
                    ...createProps.beta.onboarding,
                    otherIndustry: industry
                }
            }
        });
    }, [createProps]);

    const businessValidation = (): { disabled: boolean, guidance: string } => {

        if (!createProps?.name) {
            return { disabled: true, guidance: "Business name is required." };
        }

        if (createProps?.name?.length < 2) {
            return { disabled: true, guidance: "Business name is too short." };
        }

        if (createProps?.name?.length > 150) {
            return { disabled: true, guidance: "Business name is too long." };
        }

        return { disabled: false, guidance: "" };
    }

    const bizValid = businessValidation();

    const disabled = !createProps.template || bizValid.disabled;

    return (
        <div style={{ ...style }} className={className}>
            {error ? (
                <PageAlert background="orange" >
                    <p>We had a problem, please try again.  If it continues, please email support at support@xapp.ai</p>
                    <p>Error: {error.message}</p>
                    <p>Details:</p>
                    <pre style={{ overflow: "scroll" }}>{error.error}</pre>
                    {/**We need a call to action for them to get help */}
                </PageAlert>
            ) : (<></>)}
            {creating ? (
                <div style={{ padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <LoadingSpinner
                        textStyle={{ fontSize: "1em" }}
                        loadingText={creatingStatusMessage}
                    />
                </div>
            ) : (
                <div>
                    {welcomeMessage && (
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
                            <i>{welcomeMessage || "Welcome!"}</i>
                        </div>
                    )}
                    <h2>Tell us a little about your business...</h2>
                    <div style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", rowGap: "25px", flexDirection: "column", alignItems: "left", justifyContent: "left" }}>
                            <div>
                                <b>What is the name of your business?</b>
                                <Input
                                    variation="outlined"
                                    type={"text"}
                                    label={"Business Name"}
                                    required
                                    value={createProps?.name || ""}
                                    onChange={handleBusinessNameChange}
                                    helperText={"This should match the name of your business on your website or Google Business Profile."}
                                />
                            </div>
                            <div style={{ display: "flex", rowGap: "5px", flexDirection: "column", alignItems: "left", justifyContent: "left" }}>
                                <b>What type of business do you have?</b>
                                <p>We will use this to automatically configure some defaults for you.  Choose the one the best reflects your primary business.</p>
                                <SmartDropdown
                                    slugLabel={createProps?.template?.name || "Select Industry"}
                                    inputLabel={"Search industry..."}
                                    options={TEMPLATES}
                                    onChange={handleTemplateChange}
                                />
                                {createProps?.template?.id === "contact-capture" && (
                                    <div style={{ marginTop: "10px" }}>
                                        <b>How would you describe your business?</b>
                                        <Input
                                            label="Industry"
                                            type={"text"}
                                            autoComplete="off"
                                            helperText={"Sharing this will allow us to prioritize industries in the future"}
                                            value={createProps?.beta?.onboarding?.otherIndustry || ""}
                                            onChange={handleOtherIndustryChange}
                                        />
                                    </div>
                                )}
                            </div>
                            {/*
                            <div style={{ display: "flex", rowGap: "5px", flexDirection: "column", alignItems: "left", justifyContent: "left" }}>
                                <b>Do you have a website for your business? (Optional)</b>
                                <p>We will use content from your website to help train the AI with information about your business.</p>
                                <URLInput
                                    variation="outlined"
                                    disabled={!!initialProps?.website}
                                    label={"Business Website (Optional)"}
                                    required={!!initialProps.website}
                                    value={createProps?.website || ""}
                                    onChange={handleWebsiteChange}
                                    helperText={"The main website for your business, if you don't have a website leave it blank."}
                                />
                            </div> */}
                        </div>

                        <div style={{ padding: "20px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "right" }}>
                            {typeof onBack === "function" && (
                                <Button
                                    leftIcon="arrow_back"
                                    label={'Back'}
                                    variation="flat"
                                    onClick={onBack}
                                />
                            )}
                            <Button
                                id="stepOneNext"
                                rightIcon="arrow_forward"
                                label={`Next`}
                                variation="primary"
                                disabled={disabled}
                                onClick={handleCreateClick}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
}